import { useEffect, useRef } from 'react';
import { MapPin, UserCheck, CheckCircle2 } from 'lucide-react';

const CONFIG = {
  arrived_pickup: { bg: '#10b981', Icon: MapPin, title: 'Arrived at Pickup' },
  picked_up: { bg: '#7c3aed', Icon: UserCheck, title: 'Passenger Picked Up' },
  completed: { bg: '#0f766e', Icon: CheckCircle2, title: 'Ride Completed' },
};

let audioCtx = null;
const getAudioCtx = () => {
  if (!audioCtx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) audioCtx = new AC();
  }
  return audioCtx;
};

// Unlock audio on first user interaction (required by mobile browsers)
const unlockAudio = () => {
  const ctx = getAudioCtx();
  if (ctx && ctx.state === 'suspended') ctx.resume();
};
window.addEventListener('touchstart', unlockAudio, { once: true });
window.addEventListener('click', unlockAudio, { once: true });

const playChime = (action) => {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();
    const notes = action === 'completed' ? [880, 1174.7, 1568] : [880, 1318.5];
    const t = ctx.currentTime;
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = t + i * 0.13;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.35, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.5);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.55);
    });
  } catch (e) {
    console.warn('Audio chime failed:', e);
  }
};

export default function ActionStatusOverlay({ action, onDone, duration = 2200 }) {
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!action) return;
    if ('vibrate' in navigator) navigator.vibrate(80);
    playChime(action);
    // Timer keyed ONLY on action: parent re-renders (GPS updates) must not reset it
    const t = setTimeout(() => onDoneRef.current(), duration);
    return () => clearTimeout(t);
  }, [action, duration]);

  if (!action || !CONFIG[action]) return null;
  const { bg, Icon, title } = CONFIG[action];

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center animate-overlay-in"
      style={{ backgroundColor: bg }}
      data-testid={`action-overlay-${action}`}
    >
      <div className="w-40 h-40 rounded-full bg-white/20 flex items-center justify-center animate-overlay-pop">
        <Icon className="h-14 w-14 text-white" strokeWidth={1.6} />
      </div>
      <p className="mt-12 text-white/80 text-sm font-semibold tracking-[0.35em] uppercase">Status Updated</p>
      <h1 className="mt-2 text-white text-3xl sm:text-4xl font-bold text-center px-6">{title}</h1>
      <div className="mt-10 w-56 h-1.5 bg-white/25 rounded-full overflow-hidden">
        <div
          className="h-full bg-white rounded-full animate-overlay-progress"
          style={{ animationDuration: `${duration}ms` }}
        />
      </div>
    </div>
  );
}
